// One open page in one tab: the doc, kept in this browser, synced with the
// other tabs on it, and the text views that edit it.
import { Page, HIDDEN, DEFINITION, type NodeJSON, type Target, type TreeID } from "./doc";
import { ChannelTransport, ChaosTransport, FanTransport, Sync, type Transport } from "./sync";
import { forget, keep, load } from "./store";
import { Presence } from "./presence";
import { TextView } from "./text";
import { fixtures } from "./fixture";
import { Comments } from "./comments";
import { History } from "./history";
import { Agent, type Suggestion } from "./ai";
import { parse, print, ParseError } from "./textform";
import { keepIds, parseMarkdown, printMarkdown } from "./markdown";
import { library } from "./library";

// `file`: the page is that markdown file's text, if it's new here.
// `also`: a transport beside the tabs' channel (a room on a machine).
// `adopt`: start empty and take the page from whoever answers on it, never
// from this browser's copy or the file.
// `plain`: the file isn't markdown: the page is its text, one LoroText.
export type Options = { transport?: "channel" | "chaos"; seed?: number; fixture?: string; file?: string; also?: Transport; adopt?: boolean; plain?: boolean };

// A version is marked once a session of editing goes quiet for this long,
// and whenever the tab is hidden.
const SESSION_PAUSE = 10_000;

export class Editor {
  readonly views = new Map<TreeID, TextView>();
  readonly comments: Comments;
  readonly history: History;
  private listeners = new Set<() => void>();
  private focusNext?: { id: TreeID; at: number };
  private all: NodeJSON[] = [];
  private dirty = true;
  private named = false;
  private session?: ReturnType<typeof setTimeout>;
  selected?: TreeID;
  // More than one block, for making a component or asking the AI.
  picked: TreeID[] = [];
  // The last place text was selected, for a comment on it.
  range?: { node: TreeID; from: number; to: number };
  // A block whose `/` opened the insert menu.
  slashed?: TreeID;
  private agentOf?: Agent;

  private constructor(
    readonly id: string,
    readonly page: Page,
    readonly sync: Sync,
    readonly presence: Presence,
    private stopKeep: () => void,
    readonly plain = false,
  ) {
    this.comments = new Comments(page);
    this.history = new History(page);
    page.doc.subscribe((batch) => {
      // Text is ProseMirror's to draw; anything else redraws the page.
      if (batch.events.some((e) => e.diff.type !== "text")) this.dirty = true;
      // This tab's own edits and comments put its name in history; a
      // session's version is marked once its edits to the page pause.
      const origin = String(batch.origin ?? "");
      if (batch.by === "local" && !/^(seed|aside:(people|version))/.test(origin)) this.named ||= (this.page.name(this.page.doc.peerIdStr, this.presence.name), true);
      if (batch.by === "local" && !/^(aside|seed)/.test(origin)) this.edited();
      this.changed();
    });
    presence.onChange = () => this.changed();
    const hide = () => this.mark();
    addEventListener("pagehide", hide);
    document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && hide());
  }

  static async open(id: string, opts: Options = {}): Promise<Editor> {
    const page = new Page();
    if (opts.adopt) await forget(id); // kept, it would be merged back in on the next save
    else {
      const stored = await load(id);
      if (stored) page.doc.import(stored);
      // Two tabs starting one new page at once seed it identically.
      const start = fixtures[opts.fixture ?? "welcome"] ?? fixtures.welcome;
      if (page.fresh && opts.file !== undefined && opts.plain) page.seedText(opts.file);
      else if (page.fresh && opts.file !== undefined) {
        const read = parseMarkdown(opts.file);
        page.seed(read.nodes, {}, { tail: read.tail });
      } else if (page.fresh) page.seed(start.spec, start.tokens);
    }
    let transport: Transport = new ChannelTransport(`editor:page:${id}`);
    if (opts.transport === "chaos") transport = new ChaosTransport(transport, { drop: 0.25, duplicate: 0.25, maxDelay: 150, seed: opts.seed ?? 1 });
    if (opts.also) transport = new FanTransport([transport, opts.also]);
    const sync = new Sync(page.doc, transport, opts.transport === "chaos" ? 300 : 1000);
    const presence = new Presence(page, sync);
    return new Editor(id, page, sync, presence, keep(id, page.doc), !!opts.plain);
  }

  private roots(): NodeJSON[] {
    if (this.dirty) (this.all = this.page.roots()), (this.dirty = false);
    return this.all;
  }

  get nodes(): NodeJSON[] {
    return this.roots().filter((n) => !HIDDEN.has(n.type));
  }

  get definitions(): NodeJSON[] {
    return this.roots().filter((n) => n.type === DEFINITION);
  }

  get library() {
    return library(this.definitions);
  }

  subscribe(f: () => void): () => void {
    this.listeners.add(f);
    return () => this.listeners.delete(f);
  }

  private queued = false;
  changed() {
    if (this.queued) return;
    this.queued = true;
    queueMicrotask(() => {
      this.queued = false;
      this.listeners.forEach((f) => f());
      this.highlight();
      this.flushFocus();
    });
  }

  private edited() {
    clearTimeout(this.session);
    this.session = setTimeout(() => this.mark(), SESSION_PAUSE);
  }

  mark(): boolean {
    clearTimeout(this.session);
    return this.history.mark();
  }

  // Comments drawn over their words.
  private highlight() {
    const threads = this.comments.list().filter((t) => !t.resolved && t.from !== undefined);
    for (const [id, v] of this.views)
      v.highlight(threads.filter((t) => t.node === id).map((t) => ({ from: t.from!, to: t.to!, class: "comment-mark", id: t.id })));
  }

  private flushFocus() {
    const f = this.focusNext;
    const v = f && this.views.get(f.id);
    if (f && v) (this.focusNext = undefined), v.focus(f.at);
  }

  focus(id: TreeID, at = 0) {
    this.focusNext = { id, at };
    this.flushFocus();
  }

  // Select a block; with `add`, add it to (or take it from) the picked ones.
  select(id: TreeID | undefined, add = false) {
    if (add && id !== undefined) {
      const base = this.picked.length ? this.picked : this.selected ? [this.selected] : [];
      this.picked = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    } else this.picked = id === undefined ? [] : [id];
    this.selected = id;
    if (id) this.presence.set(id);
    this.changed();
  }

  // What the AI, a component or a comment is about: the picked blocks, or
  // the one being typed in.
  get chosen(): TreeID[] {
    if (this.picked.length) return this.picked.filter((id) => this.page.node(id));
    return this.selected && this.page.node(this.selected) ? [this.selected] : [];
  }

  mountText(id: TreeID, el: HTMLElement): () => void {
    const text = this.page.text(id);
    if (!text) return () => {};
    const view = new TextView(el, text, {
      commit: () => this.page.doc.commit({ origin: "type" }),
      undo: () => (this.page.undo.undo(), true),
      redo: () => (this.page.undo.redo(), true),
      enter: (at) => {
        // Code and lines kept as written take a line break; a cell takes none.
        const type = this.page.type(id);
        if (type === "code" || type === "markdown") {
          this.page.text(id)?.insert(at, "\n");
          this.page.doc.commit({ origin: "type" });
          this.focusNext = { id, at: at + 1 };
          return;
        }
        if (type === "cell") return;
        const next = this.page.split(id, at);
        if (next) this.focus(next, 0);
      },
      backspaceAtStart: () => {
        const r = this.page.join(id);
        if (r) this.focus(r.into, r.at);
      },
      selection: (from, to) => {
        // The panel redraws only when what it's about changes: another
        // block, or words selected. Moving a caret redraws nothing.
        const redraw = this.selected !== id || from !== to || (this.range && this.range.from !== this.range.to);
        if (this.selected !== id) this.picked = [];
        this.selected = id;
        this.range = { node: id, from, to };
        this.presence.set(id, from, to);
        if (redraw) this.changed();
      },
      keydown: (e) => this.keydown(e, id),
      slash: () => {
        this.slashed = id;
        this.changed();
      },
    });
    this.views.set(id, view);
    queueMicrotask(() => (this.flushFocus(), this.highlight()));
    return () => {
      view.destroy();
      if (this.views.get(id) === view) this.views.delete(id);
    };
  }

  // Alt+↑/↓ moves a block among its siblings; Alt+←/→ out of and into
  // the container before it.
  keydown(e: KeyboardEvent, id: TreeID): boolean {
    if (!e.altKey || e.ctrlKey || e.metaKey) return false;
    const p = this.page;
    const parent = p.parent(id);
    const i = p.index(id);
    const sibs = p.children(parent).filter((c) => !HIDDEN.has(p.type(c) ?? ""));
    let done = false;
    if (e.key === "ArrowUp" && i > 0) done = p.move(id, parent, i - 1);
    else if (e.key === "ArrowDown" && i < sibs.length - 1) done = p.move(id, parent, i + 1);
    else if (e.key === "ArrowLeft" && parent !== undefined && !HIDDEN.has(p.type(parent) ?? "")) done = p.move(id, p.parent(parent), p.index(parent) + 1);
    else if (e.key === "ArrowRight") {
      const before = sibs[i - 1];
      if (before !== undefined && p.accepts(before)) done = p.move(id, before, p.children(before).length);
    } else return false;
    if (done) {
      const at = this.views.get(id)?.view.state.selection.from ?? 0;
      this.focus(id, at);
    }
    return true;
  }

  // Insert a component after the selected block, or in place of the empty
  // one whose `/` asked for it, or at the end of the page.
  // meets: insert/library
  insert(type: string): TreeID | undefined {
    const p = this.page;
    const entry = p.entry(type);
    if (!entry) return undefined;
    const spec = { type, ...(entry.text ? { text: "" } : {}) };
    const at = this.slashed ?? this.selected;
    let id: TreeID;
    if (at !== undefined && p.node(at)) {
      id = p.insert(p.parent(at), p.index(at) + 1, spec);
      if (at === this.slashed && p.text(at)?.length === 0) p.remove(at);
    } else id = p.insert(undefined, this.nodes.length, spec);
    this.slashed = undefined;
    this.select(id);
    if (entry.text) this.focus(id, 0);
    return id;
  }

  // The source view: the page's text form, and its edits. An edit is
  // diffed against the text it was printed from and made on a copy of the
  // page as it was then, so it merges with whatever anyone did since.
  // meets: source/edit, source/live
  source(): { text: string; at: ReturnType<Page["doc"]["frontiers"]> } {
    const text = this.plain
      ? this.page.plain.toString()
      : this.markdown
      ? printMarkdown(this.page.toJSON(), String(this.page.file.get("tail")))
      : print(this.page.roots(), this.page.tokens.toJSON() as Record<string, string>);
    return { text, at: this.page.doc.frontiers() };
  }

  // A page that is a markdown file: its source is the file, ids kept here.
  get markdown(): boolean {
    return this.page.file.get("tail") !== undefined;
  }

  editSource(base: { text: string; at: ReturnType<Page["doc"]["frontiers"]> }, next: string): string | undefined {
    if (next === base.text) return undefined;
    if (this.plain) return void this.onFork(base.at, (q) => retext(q, next, "source"));
    let target: Target | undefined;
    if (!this.markdown) {
      try {
        target = parse(next);
      } catch (e) {
        return e instanceof ParseError ? e.message : String(e);
      }
    }
    // Markdown always reads; its blocks keep the ids of the blocks they were.
    const into = (q: Page) => {
      if (target) return q.match(target, "source");
      const read = parseMarkdown(next);
      keepIds(q.toJSON(), read.nodes);
      q.match({ tokens: {}, nodes: read.nodes }, "source");
      if (q.file.get("tail") !== read.tail) (q.file.set("tail", read.tail), q.doc.commit({ origin: "source" }));
    };
    const p = this.page;
    const same = JSON.stringify(p.doc.frontiers()) === JSON.stringify(base.at);
    if (same) {
      // Nobody edited since it was printed: edit the page itself, so undo
      // takes it back.
      into(p);
      return undefined;
    }
    const copy = new Page(p.doc.forkAt(base.at));
    into(copy);
    p.name(copy.doc.peerIdStr, this.presence.name);
    const update = copy.doc.export({ mode: "update", from: p.doc.frontiersToVV(base.at) });
    p.doc.import(update);
    this.sync.push(update);
    return undefined;
  }

  // The AI, a peer of its own, made the first time it's needed, and what
  // it suggested that nobody has accepted or discarded yet.
  get agent(): Agent {
    return (this.agentOf ??= new Agent(this.page, (u) => this.sync.push(u), `AI (asked by ${this.presence.name})`));
  }
  suggestion?: Suggestion;

  get aiCanUndo(): boolean {
    return !!this.agentOf?.page.undo.canUndo();
  }

  // A file changed by something else (git, another editor): its text
  // becomes the edits that make the page as it was at `at` into it, as the
  // smallest edit, with ids kept, merged with whatever was done here since.
  // Returns the version at which the page is that text.
  // meets: file/reload-keeps
  absorb(text: string, at = this.page.doc.frontiers()): ReturnType<Page["doc"]["frontiers"]> {
    if (this.plain) return this.onFork(at, (q) => retext(q, text, "file"), "the file", true);
    const p = this.page;
    const copy = new Page(p.doc.forkAt(at));
    const read = parseMarkdown(text);
    keepIds(copy.toJSON(), read.nodes);
    copy.match({ tokens: {}, nodes: read.nodes }, "file");
    if (copy.file.get("tail") !== read.tail) (copy.file.set("tail", read.tail), copy.doc.commit({ origin: "file" }));
    const update = copy.doc.export({ mode: "update", from: p.doc.frontiersToVV(at) });
    if (JSON.stringify(copy.doc.frontiers()) !== JSON.stringify(at)) {
      p.name(copy.doc.peerIdStr, "the file");
      p.doc.import(update);
      this.sync.push(update);
    }
    return copy.doc.frontiers();
  }

  // A plain page's edit, made on a copy of the page as it was at `at` and
  // merged in, so it keeps whatever anyone did since; on the page itself
  // when nobody did, so undo takes it back (unless `fork`, as the file's
  // own edits always are). Returns the version the copy ended at.
  private onFork(at: ReturnType<Page["doc"]["frontiers"]>, edit: (q: Page) => void, by = this.presence.name, fork = false): ReturnType<Page["doc"]["frontiers"]> {
    const p = this.page;
    if (!fork && JSON.stringify(p.doc.frontiers()) === JSON.stringify(at)) {
      edit(p);
      return p.doc.frontiers();
    }
    const copy = new Page(p.doc.forkAt(at));
    edit(copy);
    if (JSON.stringify(copy.doc.frontiers()) !== JSON.stringify(at)) {
      p.name(copy.doc.peerIdStr, by);
      const update = copy.doc.export({ mode: "update", from: p.doc.frontiersToVV(at) });
      p.doc.import(update);
      this.sync.push(update);
    }
    return copy.doc.frontiers();
  }

  // Bring in a page's whole history, from a file.
  merge(snapshot: Uint8Array) {
    const before = this.page.doc.oplogVersion();
    this.page.doc.import(snapshot);
    this.sync.push(this.page.doc.export({ mode: "update", from: before }));
  }

  close() {
    this.presence.close();
    this.sync.close();
    this.stopKeep();
  }
}

// A plain page's text made `text`, as the smallest edit; past what a diff
// can do in a moment (a large file, much changed), line by line.
function retext(q: Page, text: string, origin: string) {
  if (q.plain.toString() === text) return;
  try {
    q.plain.update(text, { timeoutMs: 100 });
  } catch {
    q.plain.updateByLine(text);
  }
  q.doc.commit({ origin });
}
